/**
 * Canonical pricing + feature copy for onboarding (step 4) and paywall (PricingPage).
 * PlanStep imports THIS file directly so a stale cached `plans.js` module cannot show old prices.
 *
 * Monthly list prices (Stripe products): Free $0, Owner-Operator $20/mo,
 * Fleet/Pro $40/mo base + $8 per active vehicle.
 * Stripe price IDs: STRIPE_OWNER_OP_PRICE_ID, STRIPE_FLEET_PRICE_ID,
 * STRIPE_FLEET_VEHICLE_PRICE_ID (see .env).
 */

export const FLEET_BASE_MONTHLY_DOLLARS = 40;
export const FLEET_PER_VEHICLE_MONTHLY_DOLLARS = 8;
export const FLEET_PRICE_ADDON = '+ $8/vehicle';

export function isFleetPlanTier(tier) {
  const key = (tier || '').toLowerCase();
  return key === 'fleet' || key === 'pro';
}

/** Recurring Fleet estimate: $40 + ($8 × active trucks). */
export function fleetMonthlyTotalDollars(activeVehicleCount) {
  const n = Number(activeVehicleCount);
  const qty = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  return FLEET_BASE_MONTHLY_DOLLARS + qty * FLEET_PER_VEHICLE_MONTHLY_DOLLARS;
}

/** Owner-Operator vehicle limit; paywall suggests Fleet when user exceeds this. */
export const OWNER_OPERATOR_VEHICLE_LIMIT = 2;

/** Max drivers per subscription tier. */
export const DRIVER_LIMIT_BY_TIER = {
  free: 0,
  'owner-operator': 2,
  owner_operator: 2,
  basic: 2,
  pro: 25,
  fleet: 25,
};

export function normalizePlanTierForDrivers(plan) {
  const t = (plan || 'free').toLowerCase().trim();
  if (t === 'owner_operator') return 'owner-operator';
  if (t === 'basic') return 'owner-operator';
  return t;
}

export function getDriverLimit(plan) {
  const key = normalizePlanTierForDrivers(plan);
  if (key === 'fleet' || key === 'pro') return 25;
  if (key === 'owner-operator') return 2;
  return 0;
}

/** Max vehicles per subscription tier — same ceilings as drivers. */
export function getVehicleLimit(plan) {
  return getDriverLimit(plan);
}

export function isFleetVehicleCeiling(plan) {
  const tier = normalizePlanTierForDrivers(plan);
  return tier === 'fleet' || tier === 'pro';
}

export const ENTERPRISE_CONTACT_EMAIL = 'hello@sovrynn.co';

export function getEnterpriseVehicleMailto() {
  const subject = encodeURIComponent('Enterprise pricing — Fleet vehicle limit');
  return `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=${subject}`;
}

/** Inline copy when Add Vehicle is disabled or API returns limitReached. */
export function getVehicleLimitMessage(plan) {
  const tier = normalizePlanTierForDrivers(plan);
  const limit = getVehicleLimit(tier);
  if (limit === 0) {
    return 'Vehicle tracking is not included on the Free plan. Upgrade to Owner-Operator or Fleet / Pro to add vehicles.';
  }
  if (tier === 'owner-operator') {
    return `You've reached the ${limit} vehicle limit for your plan. Upgrade to Fleet/Pro for up to 25 vehicles.`;
  }
  if (isFleetVehicleCeiling(tier)) {
    return "You've reached the Fleet plan's 25-vehicle limit. For larger fleets, contact us about Enterprise pricing.";
  }
  return `You've reached the ${limit} vehicle limit for your plan.`;
}

/** Inline copy when Add Driver is disabled or API returns limitReached. */
export function getDriverLimitMessage(plan) {
  const tier = normalizePlanTierForDrivers(plan);
  const limit = getDriverLimit(tier);
  if (limit === 0) {
    return 'Driver management is not included on the Free plan. Upgrade to Owner-Operator or Fleet / Pro to add drivers.';
  }
  if (tier === 'owner-operator') {
    return `You've reached the ${limit} driver limit for your plan. Upgrade to Fleet/Pro for up to 25 drivers.`;
  }
  return `You've reached the ${limit} driver limit for your plan.`;
}

export const FREE_PLAN_PRICE = '$0';

/** Settings badges and other in-app plan labels with monthly price. */
export const PLAN_MONTHLY_BADGE = {
  free: 'Free Plan',
  'owner-operator': 'Owner-Operator — $20/month',
  owner_operator: 'Owner-Operator — $20/month',
  basic: 'Owner-Operator — $20/month',
  pro: 'Pro — $40/mo + $8/vehicle',
  fleet: 'Fleet — $40/mo + $8/vehicle',
};

export function planMonthlyBadge(tier, activeVehicleCount) {
  const key = (tier || 'free').toLowerCase();
  const normalized = key === 'owner_operator' ? 'owner-operator' : key;
  if (
    isFleetPlanTier(normalized) &&
    activeVehicleCount != null &&
    Number.isFinite(Number(activeVehicleCount))
  ) {
    const label = normalized === 'pro' ? 'Pro' : 'Fleet';
    return `${label} — $${fleetMonthlyTotalDollars(activeVehicleCount)}/month`;
  }
  return PLAN_MONTHLY_BADGE[normalized] || PLAN_MONTHLY_BADGE.free;
}

const PAID_PLANS_CORE = [
  {
    tier: 'owner-operator',
    title: 'Owner-Operator',
    price: '$20',
    pricePeriod: 'mo',
    blurb:
      "You're the driver and the compliance department. Sovrynn handles the second job.",
    recommended: true,
    featuredOnboarding: true,
    vehicleLimit: OWNER_OPERATOR_VEHICLE_LIMIT,
    paywallCta: 'Upgrade to Owner-Operator',
    onboardingCta: 'Get Owner-Operator',
    features: [
      'Compliance Intelligence Dashboard',
      'Audit Readiness Score',
      'Compliance Timeline',
      'Vehicle Readiness Score',
      'Document expiration alerts',
      'IFTA tracking',
      'Authority compliance monitoring',
      'Rynn for DOT questions',
      'Up to 2 drivers',
    ],
  },
  {
    tier: 'fleet',
    title: 'Fleet / Pro',
    price: '$40',
    pricePeriod: 'mo',
    priceAddon: FLEET_PRICE_ADDON,
    blurb:
      'Your whole operation. One dashboard. Nothing slipping through.',
    recommended: false,
    featuredOnboarding: false,
    paywallCta: 'Upgrade to Fleet / Pro',
    onboardingCta: 'Get Fleet / Pro',
    features: [
      'Everything in Owner-Operator',
      'Up to 25 vehicles',
      'Multi-driver management',
      'Up to 25 drivers',
      'SMS alerts',
      'Advanced compliance reporting',
      'Fleet-level analytics',
      'Priority support',
    ],
  },
];

const ONBOARDING_FREE_CARD = {
  tier: 'free',
  title: 'Free',
  priceLine: '$0',
  blurb: 'Get familiar with Sovrynn. No card required.',
  featured: false,
  features: [
    'Compliance dashboard',
    'Document storage',
    'Vehicle tracking (1–2 trucks)',
    'Authority lookup',
    'Basic expiration alerts',
    'No driver management',
  ],
  ctaLabel: 'Continue with Free',
  checkoutTier: null,
};

export const ONBOARDING_CHOOSE_PLAN = {
  title: 'Your plan. Your operation.',
  description:
    'Pick what fits now. Upgrade when your business demands it.',
};

export const ONBOARDING_PLAN_CARDS = [
  ONBOARDING_FREE_CARD,
  ...PAID_PLANS_CORE.map((p) => ({
    tier: p.tier,
    title: p.title,
    priceLine: p.priceAddon
      ? `${p.price}/${p.pricePeriod} ${p.priceAddon}`
      : `${p.price}/${p.pricePeriod}`,
    blurb: p.blurb,
    featured: p.featuredOnboarding,
    features: p.features,
    ctaLabel: p.onboardingCta,
    checkoutTier: p.tier,
  })),
];

export const ONBOARDING_PLAN_LABELS = {
  free: 'Free',
  'owner-operator': 'Owner-Operator',
  fleet: 'Fleet / Pro',
};

export const PAYWALL_PLANS = PAID_PLANS_CORE.map((p) => ({
  tier: p.tier,
  title: p.title,
  price: p.price,
  period: p.pricePeriod,
  priceAddon: p.priceAddon || null,
  blurb: p.blurb,
  recommended: p.recommended,
  ctaText: p.paywallCta,
  vehicleLimit: p.vehicleLimit,
  features: [...p.features],
}));
